import { createContext, mergeProps, onMount, splitProps, useContext } from 'solid-js';
import { isServer } from 'solid-js/web';
import { insertCookieYes, insertGtmScript } from './scripts';
import type PostHog from 'posthog-js';
import type { PostHog as PostHogNode } from 'posthog-node';
import type { ParentProps } from 'solid-js';
import type { AnalyticsEvent } from './events';
import type { JsonType } from 'posthog-js';

type PostHogWeb = typeof PostHog;

type Ctx = {
	posthog: PostHogWeb | undefined;
	seenEvents: Set<`${AnalyticsEvent}-${string}`>;
	flags: Record<string, boolean | string>;
};

const Context = createContext<Ctx>();

type Props = ParentProps & {
	posthog?: PostHogWeb | PostHogNode;
	flags?: Record<string, boolean | string>;
	gtmId?: string;
	// Should consent be initialized (to deny, the standard, required default for GDPR and CCPA)
	cookieYesId?: string;
};

export function AnalyticsProvider(inputProps: Props) {
	const merged = mergeProps({ flags: {} }, inputProps);
	const [props] = splitProps(merged, ['flags', 'posthog', 'cookieYesId']) as unknown as [Ctx];

	onMount(() => {
		if (isServer) {
			return;
		}

		if (inputProps.gtmId) {
			insertGtmScript(inputProps.gtmId, inputProps.cookieYesId);
		}

		if (inputProps.cookieYesId) {
			insertCookieYes(inputProps.cookieYesId);
		}
	});

	return <Context.Provider value={{ ...props, seenEvents: new Set() }}>{inputProps.children}</Context.Provider>;
}

export function useAnalytics() {
	const ctx = useContext(Context);
	return ctx?.posthog;
}

export function useSeenEvents() {
	const ctx = useContext(Context);
	return ctx?.seenEvents;
}

export function useIsEnabled(key: string, defaultValue: boolean = false, sendEvents: boolean = true) {
	const ctx = useContext(Context);
	if (isServer) {
		return !!ctx?.flags[key];
	}

	return !!(ctx?.posthog?.isFeatureEnabled(key, { send_event: sendEvents }) ?? defaultValue);
}

export function useFeatureFlag<T extends JsonType>(
	key: string,
	defaultValue: boolean = false,
	sendEvents: boolean = true,
): [boolean, T | undefined] {
	const ctx = useContext(Context);
	const enabled = !!(ctx?.posthog?.isFeatureEnabled(key, { send_event: sendEvents }) ?? defaultValue);
	return [enabled, ctx?.posthog?.getFeatureFlagPayload(key) as T | undefined];
}

export function useExperiment(key: string, defaultBranch: string = 'control', sendEvents: boolean = true) {
	const ctx = useContext(Context);
	if (isServer) {
		return ctx!.flags[key] ?? defaultBranch;
	}

	return ctx?.posthog?.getFeatureFlag(key, { send_event: sendEvents }) ?? defaultBranch;
}
